import { Button } from '@domgen/base-design-system-2.0/atoms/Button';
import { Modal } from '@domgen/base-design-system-2.0/molecules/Modal';
import { SideDraw } from '@domgen/base-design-system-2.0/molecules/SideDraw';
import { PosthogFeatureFlags } from '@domgen/shared/enums/featureFlags';
import { calculateAgeInMonths } from '@domgen/shared/utils/date/calculateAgeInMonths';
import { formatLocalisedDate } from '@domgen/shared/utils/date/formatLocalisedDate';
import { parseLocalDateString } from '@domgen/shared/utils/date/parseLocalDateString';
import { usePostHog, useFeatureFlagEnabled } from '@posthog/react';
import { useState, useEffect } from 'react';
import { FormKey } from '../../../enums/formKey';
import { StepKey } from '../../../enums/stepKey';
import { StoreEvent } from '../../../enums/storeEvent';
import { useOTEL } from '../../../hooks/useOTEL';
import { QuoteResponse } from '../../../models/services/api/postQuote';
import { capturePostHogEvent } from '../../../services/posthog/capture';
import { useApplianceFormData } from '../../../store/selectors/useApplianceFormData';
import { useAppState } from '../../../store/selectors/useAppState';
import { useGaEventData } from '../../../store/selectors/useGaEventData';
import { useQuote } from '../../../store/selectors/useQuote';
import { useUrlParams } from '../../../store/selectors/useUrlParams';
import { store } from '../../../store/store';
import { getDiscountDisplayData } from '../../../utils/discounts/getDiscountDisplayData';
import { getSystemCode } from '../../../utils/getSystemCode';
import { enhanceSpan } from '../../../utils/otel/enhanceTrace';
import { formApplianceName } from '../../../utils/store/formApplianceName';
import { formCategoryName } from '../../../utils/store/formCategoryName';
import { ApplianceCareTerms } from '../components/ApplianceCareTerms';
import { BenefitsTable } from '../components/BenefitsTable/BenefitsTable';
import { ChooseApplianceCareArticle } from '../components/ChooseApplianceCareArticle';
import { OfferPanel } from '../components/OfferPanel';
import { StickyButtonWrapper } from '../components/StickyButtonWrapper';
import { chooseApplianceCareArticles } from '../constants/chooseApplianceCareArticles';
import { howProtectionWorksContent, ProtectionItem } from '../constants/howProtectionWorksContent';
import {
  quoteCoverageTableHeadings,
  quoteCoverageTableContent,
} from '../constants/quoteCoverageTable';
import { quoteModalContent } from '../constants/quoteModalContent';
import { getSpriteUrl } from '../../../assets/icons/spriteRegistry';
import { gaPageView, gaEvent, gaECommerceEvent } from '@domgen/shared/services/ga/ga';

const styles = {
  applianceCareTermsContainer: 'col-span-12 flex justify-center mb-space-400',
  container: 'flex flex-col items-center desktop:px-[120px] ',
  panel: 'grid grid-cols-12 max-w-[1200px] p-space-500 gap-space-600 w-full',
  headerRow: 'col-span-12 desktop:col-span-8 desktop:mr-space-400 desktop:mb-space-600',
  offerPanel1: 'col-span-12 desktop:col-start-10 desktop:col-span-4 desktop:min-w-[335px]',
  offerPanel2: 'justify-self-center desktop:min-w-[480px] mb-space-400  mx-space-500 ',
  chooseApplianceCareContainer:
    'w-full col-span-12 flex flex-col justify-center items-center gap-space-500 px-space-700 pt-space-700 mb-space-800',
  chooseApplianceCareArticles:
    'flex flex-col desktop:flex-row justify-center items-center gap-space-500 desktop:mx-space-400',
  offerPanelModal: 'desktop:max-w-[355px]',
  stickyButtonContainer: `sticky bottom-0 w-screen bg-white py-space-500 px-space-500 -mx-space-500 w-[calc(100%+var(--space-500)*2)] desktop:w-[calc(100%+240px)] desktop:-mx-[120px]`,
  buttonLayout: 'flex flex-col gap-space-500 desktop:flex-row desktop:justify-center',
  buttonLayoutModal:
    'flex flex-col w-full justify-center gap-space-500 desktop:flex-row desktop:items-center',
  buttonLayoutSideDraw: 'w-full flex flex-col gap-space-500 p-space-500 ',
  sideDrawHowProtectionWorksContainer:
    'flex flex-col gap-space-600 px-space-700 pb-space-700 pt-space-600',
  sideDrawHowProtectionWorks:
    'border-3 border-(--color-neutral-200) rounded-radius-200  py-space-400 px-space-500 ',
};

/**
 * This is the variant_3_wib for Posthog in quote-page-jul-2026 experiment.
 */
export function QuotePageWib() {
  const quote = useQuote();
  const { session } = useAppState();
  const formData = useApplianceFormData();
  const urlParams = useUrlParams();
  const systemCode = getSystemCode(urlParams?.rs);
  const [displayModal, setDisplayModal] = useState(false);
  const [displaySideDraw, setDisplaySideDraw] = useState(false);
  const posthog = usePostHog();
  const { addEvent } = useOTEL();
  const quoteData = quote?.data as QuoteResponse;
  const gaEventData = useGaEventData() || {};
  const applianceName = formApplianceName.get() ?? '';
  const applianceCategory = formCategoryName.get() ?? '';
  const brand = import.meta.env.VITE_CLIENT_FRIENDLY_NAME;
  const appSessionID = session?.id;
  const { experimentName, experimentVariant, netFirstPaymentAmount: offerPrice } = gaEventData;

  const purchaseDate = formData?.[StepKey.ARRIVAL_DATE]?.[FormKey.ARRIVAL_DATE];
  const show2MonthsPromotion =
    useFeatureFlagEnabled(PosthogFeatureFlags.TWO_MONTHS_PROP) &&
    !!purchaseDate &&
    calculateAgeInMonths(purchaseDate) < 3;

  useEffect(() => {
    addEvent('QuotePageViewed');
    enhanceSpan('olr', {
      ['session.offer']: quoteData?.offer.offerId ?? '',
      ['session.offerPrice']: offerPrice ?? '',
    });
    gaPageView({
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
      appSessionID: session?.id,
      pagename: 'protection-details',
      offerPrice,
      experimentName,
      experimentVariant,
      quoteId: quoteData.offer.offerReference,
    });
  }, []);

  const discountTerms = getDiscountDisplayData(quoteData);
  const sideDrawContent = (content: ProtectionItem) => {
    const parts = content.text.split('{ highlight }');
    //Break text on placeholder and rejoin with highlighted part in bold
    return parts.map((part, index) => (
      <span className="text-body" key={index}>
        {part}
        {index < parts.length - 1 && <span className="text-body-strong">{content.highlight}</span>}
      </span>
    ));
  };

  const toggleSideDraw = () => {
    addEvent('QuotePageSideDrawViewed');
    setDisplaySideDraw(!displaySideDraw);
  };

  const toggleModal = () => {
    addEvent('QuotePageModalViewed');

    capturePostHogEvent(posthog, 'protection-details-page-register-only-cta-clicked', store.get());

    setDisplayModal((prevDisplayModal) => {
      // Fire gaEvent only when the modal is being opened
      if (!prevDisplayModal) {
        gaEvent({
          event: 'genericGAEvent',
          eventCategory: 'appliance details form USA',
          eventAction: 'protectionDetails',
          eventLabel: 'No, I want to register with warranty only',
          pagename: 'protection-details',
          category: 'registration-pages',
          type: 'STANDARD',
          regSystemCode: systemCode,
          applianceCategory,
          applianceName,
          appSessionID,
          offerPrice,
          experimentName,
          experimentVariant,
          quoteId: quoteData.offer.offerReference,
        });

        gaPageView({
          event: 'spa-pageview',
          pagename: 'protection-details-popup',
          category: 'registration-pages',
          type: 'STANDARD',
          regSystemCode: systemCode,
          offerPrice,
          applianceName,
          applianceCategory,
          appSessionID,
          quoteId: quoteData.offer.offerReference,
        });

        gaEvent({
          event: 'genericGAEvent',
          eventCategory: 'appliance details form USA',
          eventAction: 'protectionDetails_popup_upsell',
          eventLabel: 'popup_presented',
          pagename: 'protection-details-popup',
          category: 'registration-pages',
          type: 'STANDARD',
          offerPrice,
          regSystemCode: systemCode,
          applianceCategory,
          applianceName,
          appSessionID,
          experimentName,
          experimentVariant,
          quoteId: quoteData.offer.offerReference,
        });
      }
      return !prevDisplayModal;
    });
  };

  const registerOnly = () => {
    addEvent('QuotePageRegisterOnly');
    capturePostHogEvent(posthog, 'protection-details-modal-register-only-cta-clicked', store.get());
    store.send({ type: StoreEvent.REG_ONLY });
    //Register only event only continues from the modal
    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'protection_details_popup_upsell',
      eventLabel: 'No, register with warranty only',
      pagename: 'protection-details-popup',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory,
      applianceName,
      appSessionID,
      offerPrice,
      experimentName,
      experimentVariant,
      quoteId: quoteData.offer.offerReference,
    });

    setDisplayModal(false);
    setDisplaySideDraw(false);
  };

  const continueToCheckout = () => {
    addEvent('QuotePageContinueToCheckout');
    store.send({
      type: StoreEvent.CONTINUE_TO_CHECKOUT,
      data: { continueToCheckoutFromModal: displayModal },
    });

    capturePostHogEvent(
      posthog,
      'protection-details-modal-monthly-upsell-cta-clicked',
      store.get(),
    );

    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: displayModal ? 'protectionDetails_popup_upsell' : 'protectionDetails',
      eventLabel: 'Yes, I want this protection',
      pagename: displayModal ? 'protection-details-popup' : 'protection-details',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory,
      applianceName,
      appSessionID,
      offerPrice,
      experimentName,
      experimentVariant,
      quoteId: quoteData.offer.offerReference,
    });

    gaECommerceEvent({
      event: 'add_to_cart',
      pagename: displayModal ? 'protection-details-popup' : 'protection-details',
      regSystemCode: systemCode,
      version: 'WiB',
      currency: 'USD',
      appSessionID,
      quoteId: quoteData.offer.offerReference,
      ecommerce: {
        items: [
          {
            item_brand: brand,
            item_category: applianceCategory,
            item_appliance_type: applianceName,
            item_appliance_purchase_cost: Number(
              formData?.[StepKey.PURCHASE_PRICE]?.[FormKey.PURCHASE_PRICE],
            ),
            item_appliance_purchase_date: formatLocalisedDate(
              parseLocalDateString(formData?.[StepKey.ARRIVAL_DATE]?.[FormKey.ARRIVAL_DATE] || ''),
              'en-US',
            ),
            item_appliance_warranty: true,
            item_name: `${brand} ${applianceName}`,
            price: offerPrice,
            quantity: 1,
            quoteId: quoteData.offer.offerReference,
          },
        ],
      },
    });
  };
  const howProtectionCareWorks = howProtectionWorksContent(getSpriteUrl(brand));

  return (
    <>
      <div className={styles.container} data-testid="quote-page-wib">
        <div className={styles.panel}>
          <div className={styles.headerRow}>
            <div>
              <p className="mb-space-400 ">While we process your registration...</p>
              <h1 className="mb-space-400 text-h3">
                Protect your {applianceName?.toLowerCase()} for things not covered by your warranty
              </h1>
              <p>
                Your standard warranty only covers mechanical or electrical breakdowns. Get extra
                protection for
                <strong> accidental damage cover</strong> - plus
                <strong> unlimited repairs</strong> and <strong>a replacement appliance</strong> if
                it can’t be fixed.
              </p>

              <div className="w-fit mt-space-100">
                <button
                  onClick={() => {
                    toggleSideDraw();
                    capturePostHogEvent(
                      posthog,
                      'protection-details-display-side-drawer',
                      store.get(),
                    );
                  }}
                  type="button"
                  className="cursor-pointer text-body-link-strong"
                  aria-label="View how extra protection works"
                >
                  See how it works
                </button>
              </div>
            </div>
          </div>

          <div className={styles.offerPanel1}>
            <OfferPanel
              periodOfCover={discountTerms.periodOfCover}
              discountPercentage={discountTerms.discountPercentage}
              discountTerm={discountTerms.discountTerm}
              netFirstPaymentAmount={discountTerms.netFirstPaymentAmount}
              netFirstPaymentAmountAfterPromotionalPeriod={
                discountTerms.netFirstPaymentAmountAfterPromotionalPeriod
              }
              discountPaymentType={discountTerms.discountPaymentType}
              show2MonthsPromotion={show2MonthsPromotion}
            />
          </div>

          <div className="col-span-12 ">
            <h2 className="text-h6 mb-space-400 self-start">
              Here's how extra protection compares to the manufacturer's warranty:
            </h2>
            <BenefitsTable
              headings={quoteCoverageTableHeadings}
              content={quoteCoverageTableContent}
            />
          </div>

          <div className={styles.applianceCareTermsContainer}>
            <ApplianceCareTerms
              href={import.meta.env.VITE_APPLIANCE_CARE_TERMS_AND_CONDITIONS_PATH}
              brand={import.meta.env.VITE_CLIENT_FRIENDLY_NAME}
            />
          </div>
        </div>
        <div className={styles.offerPanel2}>
          <OfferPanel
            periodOfCover={discountTerms.periodOfCover}
            discountPercentage={discountTerms.discountPercentage}
            discountTerm={discountTerms.discountTerm}
            netFirstPaymentAmount={discountTerms.netFirstPaymentAmount}
            netFirstPaymentAmountAfterPromotionalPeriod={
              discountTerms.netFirstPaymentAmountAfterPromotionalPeriod
            }
            discountPaymentType={discountTerms.discountPaymentType}
            show2MonthsPromotion={show2MonthsPromotion}
          />
        </div>

        <StickyButtonWrapper
          containerClasses="sticky bottom-0 w-screen bg-white py-space-500 px-space-500 -mx-space-500 w-[calc(100%+var(--space-500)*2)] desktop:w-[calc(100%+240px)] desktop:-mx-[120px]"
          scrollContainer="window"
          stickyElementSelector="[data-sticky-button]"
        >
          <div className={styles.buttonLayout} data-sticky-button>
            <div className="inline-flex flex-none">
              <Button
                variant="outline"
                onClick={() => {
                  toggleModal();
                }}
              >
                No, I want to continue with registration only
              </Button>
            </div>
            <div className="inline-flex flex-none">
              <Button
                variant="primary"
                onClick={() => {
                  capturePostHogEvent(
                    posthog,
                    'protection-details-page-monthly-upsell-cta-clicked',
                    store.get(),
                  );
                  continueToCheckout();
                }}
              >
                Yes, I want this protection
              </Button>
            </div>
          </div>
        </StickyButtonWrapper>

        <div className={styles.chooseApplianceCareContainer}>
          <h5 className="text-center text-h5 desktop:text-left">
            Why choose {brand} Appliance Care?
          </h5>

          <div className={styles.chooseApplianceCareArticles}>
            {chooseApplianceCareArticles.map((article, index) => (
              <ChooseApplianceCareArticle
                icon={article.icon}
                description={article.description}
                key={index}
              />
            ))}
          </div>
        </div>
      </div>

      <SideDraw isOpen={displaySideDraw} onClose={toggleSideDraw}>
        <div className={styles.sideDrawHowProtectionWorksContainer}>
          <div className={styles.sideDrawHowProtectionWorks}>
            <h5 className="text-h5 mb-space-200">{howProtectionCareWorks.title}</h5>
            <div className="flex flex-col justify-start items-center gap-space-100">
              {howProtectionCareWorks.protection.map((content, index: number) => {
                return (
                  <div key={index} className="flex flex-row items-center gap-2">
                    <span>{content.icon}</span>
                    <div>
                      <h3 className="text-body-large-strong">{content.heading}</h3>
                      {sideDrawContent(content)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <OfferPanel
            periodOfCover={discountTerms.periodOfCover}
            discountPercentage={discountTerms.discountPercentage}
            discountTerm={discountTerms.discountTerm}
            netFirstPaymentAmount={discountTerms.netFirstPaymentAmount}
            netFirstPaymentAmountAfterPromotionalPeriod={
              discountTerms.netFirstPaymentAmountAfterPromotionalPeriod
            }
            discountPaymentType={discountTerms.discountPaymentType}
            show2MonthsPromotion={show2MonthsPromotion}
          />

          <ApplianceCareTerms
            href={import.meta.env.VITE_APPLIANCE_CARE_TERMS_AND_CONDITIONS_PATH}
            brand={import.meta.env.VITE_CLIENT_FRIENDLY_NAME}
          />
        </div>

        <StickyButtonWrapper>
          <div className={styles.buttonLayoutSideDraw}>
            <Button
              variant="outline"
              onClick={() => {
                registerOnly();
                capturePostHogEvent(
                  posthog,
                  'protection-details-side-drawer-register-only-cta-clicked',
                  store.get(),
                );
              }}
            >
              No, I want to continue with registration only
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                continueToCheckout();
                capturePostHogEvent(
                  posthog,
                  'protection-details-side-drawer-monthly-upsell-cta-clicked',
                  store.get(),
                );
              }}
            >
              Yes, I want this protection
            </Button>
          </div>
        </StickyButtonWrapper>
      </SideDraw>

      <Modal isOpen={displayModal} onClose={toggleModal}>
        <h1 className="text-h5  mb-space-400">{quoteModalContent.title}</h1>
        <p className="mb-space-400">{quoteModalContent.description}</p>
        <p className="text-body-strong mb-space-200">{quoteModalContent.benefits.heading}</p>
        <ul className="list-none mb-4 text-left">
          {quoteModalContent.benefits.benefitsList.map((benefit, index) => (
            <li key={index} className="flex items-center mb-2">
              <span className="mr-2">{<benefit.icon />}</span>
              <span>{benefit.text}</span>
            </li>
          ))}
        </ul>
        <div className="flex flex-col items-center gap-space-400 ">
          <div className={styles.offerPanelModal}>
            <OfferPanel
              periodOfCover={discountTerms.periodOfCover}
              discountPercentage={discountTerms.discountPercentage}
              discountTerm={discountTerms.discountTerm}
              netFirstPaymentAmount={discountTerms.netFirstPaymentAmount}
              netFirstPaymentAmountAfterPromotionalPeriod={
                discountTerms.netFirstPaymentAmountAfterPromotionalPeriod
              }
              discountPaymentType={discountTerms.discountPaymentType}
              show2MonthsPromotion={show2MonthsPromotion}
            />
          </div>

          <div className={styles.buttonLayoutModal}>
            <Button
              variant="outline"
              onClick={() => {
                registerOnly();
              }}
            >
              No thanks, register only
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                continueToCheckout();
              }}
            >
              Register with extra coverage
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
